import type { ClassInfo } from '@/types/class'
import type { CraftRecipe } from '@/types/crafting'
import type { EnhancementRule } from '@/types/enhancement'
import type { Gem } from '@/types/gem'
import type { Item } from '@/types/item'
import type { Skill } from '@/types/skill'
import type { StatsConfig } from '@/types/statsConfig'
import { sanitizeSkillDescription } from '@/utils/sanitizeText'

const BASE_URL = import.meta.env.BASE_URL
const DATA_VERSION = import.meta.env.VITE_ARMORY_DATA_VERSION ?? ''

function appendVersion(url: string) {
  if (!DATA_VERSION) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}v=${encodeURIComponent(DATA_VERSION)}`
}

function buildPublicUrl(path: string) {
  const cleanPath = path.replace(/^\/+/, '')
  return appendVersion(`${BASE_URL}${cleanPath}`)
}

async function fetchJson<T>(path: string): Promise<T> {
  const url = buildPublicUrl(path)
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`)
  }
  return (await response.json()) as T
}

type ItemsPayload = { items: Item[] }
type SkillsPayload = { skills: Skill[] }
type ClassesPayload = { classes: ClassInfo[] }
type CraftRecipesPayload = { recipes: CraftRecipe[] }
type EnhancementsPayload = { enhancements: EnhancementRule[] }
type GemsPayload = { gems: Gem[] }
type StatsConfigPayload = StatsConfig

function buildCraftableItemIdSet(recipes: CraftRecipe[]) {
  const craftableIds = new Set<string>()
  for (const recipe of recipes) {
    if (!recipe?.id) continue
    craftableIds.add(recipe.id)
  }
  return craftableIds
}

let itemsCache: Item[] | null = null
let itemsPromise: Promise<Item[]> | null = null

export function loadItems(): Promise<Item[]> {
  if (itemsCache) return Promise.resolve(itemsCache)
  if (itemsPromise) return itemsPromise
  itemsPromise = fetchJson<ItemsPayload>('data/items.json')
    .then(async (payload) => {
      const baseItems = (payload.items ?? []).filter((item) => item.type !== 'cosmetic')

      let craftableIds = new Set<string>()
      try {
        const recipes = await loadCraftRecipes()
        craftableIds = buildCraftableItemIdSet(recipes)
      } catch {
        // Keep item loading resilient even if crafting payload is unavailable.
      }

      itemsCache = baseItems.map((item) => ({
        ...item,
        acquisitionSource: craftableIds.has(item.id) ? 'craft' : 'drop',
      }))

      return itemsCache
    })
    .catch((err) => {
      itemsPromise = null
      throw err
    })
  return itemsPromise
}

let skillsCache: Skill[] | null = null
let skillsPromise: Promise<Skill[]> | null = null

export function loadSkills(): Promise<Skill[]> {
  if (skillsCache) return Promise.resolve(skillsCache)
  if (skillsPromise) return skillsPromise
  skillsPromise = fetchJson<SkillsPayload>('data/skills.json')
    .then((payload) => {
      skillsCache = (payload.skills ?? [])
        // Auto-attack skills are intentionally hidden (not useful for the Armory UX).
        .filter((skill) => skill.type !== 'auto')
        .map((skill) => ({
          ...skill,
          description: sanitizeSkillDescription(skill.description),
        }))
      return skillsCache
    })
    .catch((err) => {
      skillsPromise = null
      throw err
    })
  return skillsPromise
}

let craftRecipesCache: CraftRecipe[] | null = null
let craftRecipesPromise: Promise<CraftRecipe[]> | null = null

export function loadCraftRecipes(): Promise<CraftRecipe[]> {
  if (craftRecipesCache) return Promise.resolve(craftRecipesCache)
  if (craftRecipesPromise) return craftRecipesPromise
  craftRecipesPromise = fetchJson<CraftRecipesPayload>('data/crafting.json')
    .then((payload) => {
      craftRecipesCache = payload.recipes ?? []
      return craftRecipesCache
    })
    .catch((err) => {
      craftRecipesPromise = null
      throw err
    })
  return craftRecipesPromise
}

let classesCache: ClassInfo[] | null = null
let classesPromise: Promise<ClassInfo[]> | null = null

export function loadClasses(): Promise<ClassInfo[]> {
  if (classesCache) return Promise.resolve(classesCache)
  if (classesPromise) return classesPromise
  classesPromise = fetchJson<ClassesPayload>('data/classes.json')
    .then((payload) => {
      classesCache = payload.classes ?? []
      return classesCache
    })
    .catch((err) => {
      classesPromise = null
      throw err
    })
  return classesPromise
}

let enhancementsCache: EnhancementRule[] | null = null
let enhancementsPromise: Promise<EnhancementRule[]> | null = null

export function loadEnhancements(): Promise<EnhancementRule[]> {
  if (enhancementsCache) return Promise.resolve(enhancementsCache)
  if (enhancementsPromise) return enhancementsPromise
  enhancementsPromise = fetchJson<EnhancementsPayload>('data/enhancements.json')
    .then((payload) => {
      enhancementsCache = payload.enhancements ?? []
      return enhancementsCache
    })
    .catch((err) => {
      enhancementsPromise = null
      throw err
    })
  return enhancementsPromise
}

let gemsCache: Gem[] | null = null
let gemsPromise: Promise<Gem[]> | null = null

export function loadGems(): Promise<Gem[]> {
  if (gemsCache) return Promise.resolve(gemsCache)
  if (gemsPromise) return gemsPromise
  gemsPromise = fetchJson<GemsPayload>('data/gems.json')
    .then((payload) => {
      gemsCache = payload.gems ?? []
      return gemsCache
    })
    .catch((err) => {
      gemsPromise = null
      throw err
    })
  return gemsPromise
}

let statsConfigCache: StatsConfig | null = null
let statsConfigPromise: Promise<StatsConfig> | null = null

export function loadStatsConfig(): Promise<StatsConfig> {
  if (statsConfigCache) return Promise.resolve(statsConfigCache)
  if (statsConfigPromise) return statsConfigPromise
  statsConfigPromise = fetchJson<StatsConfigPayload>('data/stats_config.json')
    .then((payload) => {
      statsConfigCache = payload
      return statsConfigCache
    })
    .catch((err) => {
      statsConfigPromise = null
      throw err
    })
  return statsConfigPromise
}
